import { useState } from "react";
import { requestAccess, ApiError } from "../api";

/**
 * "Request access" screen (route `/request-access`, anonymous). Reached from the landing's maintenance
 * banner and the "sign-ups paused" turn-away — a would-be user leaves their email so an operator can
 * invite them in while a maintenance tier is on. Submits to the public, rate-limited endpoint. On success
 * it swaps to a neutral thank-you (never revealing whether the email already has an account).
 */
export function RequestAccess() {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending");
    setError(null);
    try {
      await requestAccess(email.trim(), note);
      setState("done");
    } catch (err) {
      setState("idle");
      setError(err instanceof ApiError ? err.message : "Something went wrong — please try again.");
    }
  };

  return (
    <div className="gate">
      <div className="gate-card" role="form">
        <div className="brand big">Amendie</div>
        {state === "done" ? (
          <>
            <h2 className="gate-title">Thanks — you&rsquo;re on the list</h2>
            <p className="muted">
              We&rsquo;ll email <b>{email.trim()}</b> as soon as we can let you in. No account is created
              yet — nothing more to do.
            </p>
            <a className="btn lg" href="/">
              Back to home
            </a>
          </>
        ) : (
          <>
            <h2 className="gate-title">Request access</h2>
            <p className="muted">
              We&rsquo;re not taking new accounts right now. Leave your email and we&rsquo;ll reach out
              when access opens.
            </p>
            {/* Stacked full-width fields (a narrow gate card, not the wide admin form-grid) so the inputs
                fill the card and line up with the buttons below. */}
            <form
              onSubmit={submit}
              className="mt"
              style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: "var(--sp4)" }}
            >
              <div>
                <label htmlFor="ra-email" className="muted sm" style={{ display: "block", marginBottom: 4 }}>
                  Email
                </label>
                <input
                  id="ra-email"
                  type="email"
                  required
                  autoFocus
                  placeholder="you@company.com"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <label htmlFor="ra-note" className="muted sm" style={{ display: "block", marginBottom: 4 }}>
                  Note (optional)
                </label>
                <textarea
                  id="ra-note"
                  rows={3}
                  placeholder="what you'd like to scrape"
                  value={note}
                  onChange={(ev) => setNote(ev.target.value)}
                  style={{ width: "100%", resize: "vertical" }}
                />
              </div>
            </form>
            {error && <div className="err mt">{error}</div>}
            <div className="row-gap" style={{ justifyContent: "center", marginTop: "var(--sp4)" }}>
              <button className="btn lg" onClick={submit} disabled={state === "sending" || !email.trim()}>
                {state === "sending" ? "Sending…" : "Request access"}
              </button>
              <a className="btn ghost lg" href="/">
                Back
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
